import { FC, useMemo } from 'react'
import {
  ArrayInput, Edit, NumberInput, RecordContextProvider, required, SelectInput, SimpleForm,
  SimpleFormIterator, TextInput, useRecordContext, useTranslate,
} from 'react-admin'
import { InputAdornment, Typography } from '@mui/material'
import { OrganizationOut } from '../../types/apiRequests'

const formToApi = (data: Record<string, unknown>) => {
  const entries = data.metadata_entries as Array<{ key: string; value: string }> | undefined
  const metadataFields: Record<string, string> = {}
  if (entries) {
    for (const entry of entries) {
      if (entry.key) metadataFields[entry.key] = entry.value || ''
    }
  }
  // eslint-disable-next-line camelcase
  const { metadata_entries: _, ...rest } = data
  // eslint-disable-next-line camelcase
  return { ...rest, metadata_fields: metadataFields }
}

const TransformedForm: FC = () => {
  const record = useRecordContext<OrganizationOut>()
  const translate = useTranslate()

  const transformedRecord = useMemo(() => {
    if (!record) return undefined
    const entries = record.metadata_fields
      ? Object.entries(record.metadata_fields).map(([key, value]) => ({ key, value: String(value) }))
      : []
    return { ...record, metadata_entries: entries } // eslint-disable-line camelcase
  }, [record])

  if (!transformedRecord) return null

  return (
    <RecordContextProvider value={transformedRecord}>
      <SimpleForm>
        <TextInput source="display_name" label="data.displayName" fullWidth validate={required()} />
        <TextInput source="name" label="data.name" fullWidth disabled />
        <TextInput source="idp_org_id" label="data.auth0OrgId" fullWidth disabled />
        <TextInput source="description" label="data.description" fullWidth multiline />
        <SelectInput
          source="hub_tier"
          label="filters.tier"
          choices={[
            { id: 'none', name: translate('filters.tierNone') },
            { id: 'free', name: translate('filters.tierFree') },
            { id: 'team', name: translate('filters.tierTeam') },
            { id: 'enterprise', name: translate('filters.tierEnterprise') },
          ]}
          fullWidth
        />
        <NumberInput
          source="cost_per_seat"
          label="organizationCosts.costPerSeat"
          helperText={translate('organizationCosts.costPerSeatHelp')}
          fullWidth
          InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
        />
        <NumberInput
          source="cost_per_mcp"
          label="organizationCosts.costPerMcp"
          helperText={translate('organizationCosts.costPerMcpHelp')}
          fullWidth
          InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
        />
        <NumberInput
          source="cost_per_workflow"
          label="organizationCosts.costPerWorkflow"
          helperText={translate('organizationCosts.costPerWorkflowHelp')}
          fullWidth
          InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
        />
        <NumberInput
          source="ai_quota_eur_per_hour"
          label="organizationCosts.aiQuotaEurPerHour"
          helperText={translate('organizationCosts.aiQuotaEurPerHourHelp')}
          min={0}
          step={0.01}
          fullWidth
          InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
        />
        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
          {translate('data.metadataFields')}
        </Typography>
        <ArrayInput source="metadata_entries" label="">
          <SimpleFormIterator inline disableReordering>
            <TextInput source="key" label="data.metadataKey" />
            <TextInput source="value" label="data.metadataValue" />
          </SimpleFormIterator>
        </ArrayInput>
      </SimpleForm>
    </RecordContextProvider>
  )
}

const OrganizationEdit: FC = () => (
  <Edit mutationMode="pessimistic" transform={formToApi}>
    <TransformedForm />
  </Edit>
)

export default OrganizationEdit
